import { Suspense, lazy, type ComponentType } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import AppLayout from './AppLayout.js';
import RequireAuth from './auth/RequireAuth.js';
const LoginPage = routeChunk(() => import('./pages/LoginPage.js'));
const SitesIndexPage = routeChunk(() => import('./pages/SitesIndexPage.js'));
const NewSitePage = routeChunk(() => import('./pages/NewSitePage.js'));
const AdminPage = routeChunk(() => import('./pages/AdminPage.js'));
const ChangelogPage = routeChunk(() => import('./pages/ChangelogPage.js'));
const AccountSettingsPage = routeChunk(() => import('./pages/AccountSettingsPage.js'));
const SiteDetailPage = routeChunk(() => import('./pages/SiteDetailPage.js'));
const SiteEditPage = routeChunk(() => import('./pages/SiteEditPage.js'));
const SiteInquiriesPage = routeChunk(() => import('./pages/SiteInquiriesPage.js'));
const SiteAnalyticsPage = routeChunk(() => import('./pages/SiteAnalyticsPage.js'));
const SiteGeneratePage = routeChunk(() => import('./pages/SiteGeneratePage.js'));
const SiteOnboardingPage = routeChunk(() => import('./pages/SiteOnboardingPage.js'));
const SitePrecheckPage = routeChunk(() => import('./pages/SitePrecheckPage.js'));
const SiteTechConfigPage = routeChunk(() => import('./pages/SiteTechConfigPage.js'));
const PlaceholderPage = routeChunk(() => import('./pages/crm/PlaceholderPage.js'));
const InboxPage = routeChunk(() => import('./pages/crm/InboxPage.js'));
const InboxDetailPage = routeChunk(() => import('./pages/crm/InboxDetailPage.js'));
const DraftsPage = routeChunk(() => import('./pages/crm/DraftsPage.js'));
/** 飞书询盘卡片的落地跳板：询盘 id → 这条询盘的客户会话页 */
const InquiryRedirectPage = routeChunk(() => import('./pages/crm/InquiryRedirectPage.js'));
const PushLogPage = routeChunk(() => import('./pages/crm/PushLogPage.js'));
const TemplatesPage = routeChunk(() => import('./pages/crm/TemplatesPage.js'));
const PushRulesPage = routeChunk(() => import('./pages/crm/PushRulesPage.js'));
const BlocksPage = routeChunk(() => import('./pages/crm/BlocksPage.js'));
const BlocksAuditPage = routeChunk(() => import('./pages/crm/BlocksAuditPage.js'));
const TenantNotifyLogPage = routeChunk(() => import('./pages/crm/TenantNotifyLogPage.js'));
const GatewayPage = routeChunk(() => import('./pages/crm/GatewayPage.js'));
const OutreachFunnelPage = routeChunk(() => import('./pages/crm/OutreachFunnelPage.js'));
const FactoryOutreachPage = routeChunk(() => import('./pages/crm/FactoryOutreachPage.js'));
const InboundChannelStatusPage = routeChunk(
  () => import('./pages/crm/InboundChannelStatusPage.js'),
);
const InquiryMailboxesPage = routeChunk(() => import('./pages/crm/InquiryMailboxesPage.js'));
const InboundEndpointsPage = routeChunk(() => import('./pages/crm/InboundEndpointsPage.js'));
const FieldCandidatesPage = routeChunk(() => import('./pages/crm/FieldCandidatesPage.js'));
const TenantsPage = routeChunk(() => import('./pages/crm/TenantsPage.js'));
const FactoryOnboardingPage = routeChunk(() => import('./pages/crm/FactoryOnboardingPage.js'));
const TenantAssignmentPage = routeChunk(() => import('./pages/crm/TenantAssignmentPage.js'));
const TierCapabilitiesPage = routeChunk(() => import('./pages/crm/TierCapabilitiesPage.js'));
const TenantDetailPage = routeChunk(() => import('./pages/crm/TenantDetailPage.js'));
const OrderFlowsPage = routeChunk(() => import('./pages/crm/OrderFlowsPage.js'));
const OrderFlowEditPage = routeChunk(() => import('./pages/crm/OrderFlowEditPage.js'));
const OrdersPage = routeChunk(() => import('./pages/crm/OrdersPage.js'));
const QuotesPage = routeChunk(() => import('./pages/crm/QuotesPage.js'));
const OrderCreatePage = routeChunk(() => import('./pages/crm/OrderCreatePage.js'));
const OrderImportPage = routeChunk(() => import('./pages/crm/OrderImportPage.js'));
const OrderDetailPage = routeChunk(() => import('./pages/crm/OrderDetailPage.js'));
const PurchaseOrdersPage = routeChunk(() => import('./pages/crm/PurchaseOrdersPage.js'));
const FeishuApprovalsPage = routeChunk(() => import('./pages/crm/FeishuApprovalsPage.js'));
const BindingDashboardPage = routeChunk(() => import('./pages/crm/BindingDashboardPage.js'));
const BindLandingPage = routeChunk(() => import('./pages/wechat/BindLandingPage.js'));
import { useAuth } from './auth/AuthContext.js';
import PortalLayout from './portal/PortalLayout.js';
import PortalLoginPage from './portal/PortalLoginPage.js';
import { isPortalHost } from './lib/host.js';
const PortalHomePage = routeChunk(() => import('./portal/pages/PortalHomePage.js'));
const PortalConversationPage = routeChunk(() => import('./portal/pages/PortalConversationPage.js'));

/**
 * 会话页按 `customer_id` **重挂**，不复用上一个客户的实例。
 *
 * 会话页有一堆局部状态（输入框正文、找货结果、草稿卡展开、撤销那一步、错误提示），
 * react-router 在同一条 Route 上换 param 是**不会重挂组件的** —— 从客户 1 直接点到
 * 客户 2，右栏还显示着上一个客户的找货结果、草稿卡直接是展开的、输入框里还留着
 * 给上一个客户写了一半的话。最后那条最危险：他很可能直接就发出去了。
 *
 * 用 `key` 而不是在页面里逐个 reset：状态会一直加（这一单就加了五个），
 * 逐个 reset 是「每加一个记得补一行」，而 key 是结构上做完。
 */
function LegacyLeadRedirect(): JSX.Element {
  const { id = '' } = useParams();
  return <Navigate to={`/strand/inquiries/${encodeURIComponent(id)}`} replace />;
}

function KeyedPortalConversationPage(): JSX.Element {
  const { id = '' } = useParams();
  return <PortalConversationPage key={id} />;
}
const SelfOperatedPortal = routeChunk(() => import('./portal/SelfOperatedPortal.js'));
const PortalOrdersPage = routeChunk(() => import('./portal/pages/PortalOrdersPage.js'));
const PortalCustomersPage = routeChunk(() => import('./portal/pages/PortalCustomersPage.js'));
const PortalCustomerNewPage = routeChunk(() => import('./portal/pages/PortalCustomerNewPage.js'));
const PortalSitePage = routeChunk(() => import('./portal/pages/PortalSitePage.js'));
const PortalCustomerImportPage = routeChunk(
  () => import('./portal/pages/PortalCustomerImportPage.js'),
);
const PortalAccountPage = routeChunk(() => import('./portal/pages/PortalAccountPage.js'));
const PortalNotifySettingsPage = routeChunk(
  () => import('./portal/pages/PortalNotifySettingsPage.js'),
);
const PortalWhatsAppPage = routeChunk(() => import('./portal/pages/PortalWhatsAppPage.js'));
const PortalSocialPage = routeChunk(() => import('./portal/pages/PortalSocialPage.js'));
const PortalChannelsPage = routeChunk(() => import('./portal/pages/PortalChannelsPage.js'));
const WaRelayStatusPage = routeChunk(() => import('./pages/crm/WaRelayStatusPage.js'));
const WaSelfBindPage = routeChunk(() => import('./pages/crm/WaSelfBindPage.js'));
const WaEgressPage = routeChunk(() => import('./pages/crm/WaEgressPage.js'));
const SocialCommentsPage = routeChunk(() => import('./pages/crm/SocialCommentsPage.js'));
const ChannelsOverviewPage = routeChunk(() => import('./pages/crm/ChannelsOverviewPage.js'));
const ErAuditPage = routeChunk(() => import('./pages/crm/ErAuditPage.js'));
const EscalationPage = routeChunk(() => import('./pages/crm/EscalationPage.js'));
const DecisionsPage = routeChunk(() => import('./pages/crm/DecisionsPage.js'));
const SuppliersPage = routeChunk(() => import('./pages/crm/SuppliersPage.js'));
const SupplierDetailPage = routeChunk(() => import('./pages/crm/SupplierDetailPage.js'));
const SupplierHealthPage = routeChunk(() => import('./pages/crm/SupplierHealthPage.js'));
const ProductsPage = routeChunk(() => import('./pages/crm/ProductsPage.js'));
const ProductDetailPage = routeChunk(() => import('./pages/crm/ProductDetailPage.js'));
const DashboardPage = routeChunk(() => import('./pages/crm/DashboardPage.js'));
const MatchPlaygroundPage = routeChunk(() => import('./pages/crm/MatchPlaygroundPage.js'));
const SalespeoplePage = routeChunk(() => import('./pages/crm/SalespeoplePage.js'));
const CasePage = routeChunk(() => import('./pages/crm/CasePage.js'));
const DraftEditPage = routeChunk(() => import('./pages/crm/DraftEditPage.js'));
const MigrationListPage = routeChunk(() => import('./pages/crm/MigrationListPage.js'));
const MigrationDetailPage = routeChunk(() => import('./pages/crm/MigrationDetailPage.js'));
const SourcingListPage = routeChunk(() => import('./pages/crm/SourcingListPage.js'));
const SourcingDetailPage = routeChunk(() => import('./pages/crm/SourcingDetailPage.js'));
const CompetitorsPage = routeChunk(() => import('./pages/seo/CompetitorsPage.js'));
const CitationsPage = routeChunk(() => import('./pages/seo/CitationsPage.js'));
const ReportDetailPage = routeChunk(() => import('./pages/seo/ReportDetailPage.js'));
const TopicsPage = routeChunk(() => import('./pages/seo/TopicsPage.js'));
const RunsPage = routeChunk(() => import('./pages/seo/RunsPage.js'));
const ArticleIndexPage = routeChunk(() => import('./pages/seo/ArticleIndexPage.js'));
const TaskListPage = routeChunk(() => import('./pages/agent/TaskListPage.js'));
const TaskDetailPage = routeChunk(() => import('./pages/agent/TaskDetailPage.js'));
import { getAllModuleChildren } from './modules/registry.js';

/**
 * 路由页按需加载。
 *
 * ── 为什么加这个 ─────────────────────────────────────────────
 *
 * 改之前 admin 是一整包:六十多个内部页面 + 工厂端八页,构建产物 1.1 MB
 * (gzip 330 KB)。工厂老板在手机 4G 上打开登录页,要把整个内部后台先下下来
 * 才看得到第一屏 —— 实测 Lighthouse mobile 的 LCP 6.2 秒,而对外页面的
 * 验收线是 3 秒。
 *
 * 这不是把内部后台改了:每一页的代码一个字没动,只是从「进门就全下」
 * 改成「点到哪下哪」。内部业务员那边第一次点进某一页会多一次几十 KB 的请求,
 * 之后走缓存;换来的是工厂端首屏少下 700 KB。
 *
 * ── 为什么每页各自一个 Suspense,不在 <Routes> 外面套一个 ────────
 *
 * 套在外面的话,内部后台每切一次页,连同左侧栏和顶栏一起被 fallback 顶掉,
 * 整个界面闪一下。各自一个边界,壳不动,只有内容区在等。
 *
 * `PortalLoginPage` **不走这里**(还是静态 import):它就是那个要快的页面,
 * 给它再加一次网络往返是反的。
 */
/**
 * 部署之后，**已经开着的那个标签页**加载不到新的分片。
 *
 * 分片文件名带哈希，一次部署换一批；而 SPA 是在你点进某一页的那一刻才去取它那个分片。
 * 所以：早上开着的页面，中午发一次版，下午点进一个没去过的页面 → 那个分片的文件名
 * 已经是上一版的、服务器上没有了 → `Failed to fetch dynamically imported module`，
 * 页面白屏。09-21 实测到的就是这个（发两次版，第一版的 WaSelfBindPage 分片 404）。
 *
 * 两道一起上：
 *   服务端 `builder.sh activate` 把前几版的 assets 并进新版（老文件名照样取得到）
 *   这里兜底：取不到就**自动刷新一次**，新的 index.html 会带来新的分片名
 *
 * 只刷一次（sessionStorage 按分片记一个标记）—— 真的是分片没了，刷新能好；
 * 要是新版本身就坏了，刷新救不了，那就必须停下来让人看见报错，不能无限刷。
 */
function withChunkReload<P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>,
): () => Promise<{ default: ComponentType<P> }> {
  return async () => {
    try {
      // 网络上挂住的请求不会失败、只会一直等 —— 页面就永远停在「加载中」。
      // 15 秒没回来按失败算，走下面「刷新一次」那条路（10-22 工厂端消息页实测挂住过）
      return await Promise.race([
        load(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('chunk load timeout')), 15_000),
        ),
      ]);
    } catch (err) {
      const key = `chunk-reloaded:${String(err).slice(0, 120)}`;
      let alreadyTried = true;
      try {
        alreadyTried = sessionStorage.getItem(key) === '1';
        if (!alreadyTried) sessionStorage.setItem(key, '1');
      } catch {
        // 无痕模式之类读不到 sessionStorage：那就别刷（宁可报错，也不要刷新死循环）
        alreadyTried = true;
      }
      if (!alreadyTried) {
        window.location.reload();
        // 刷新是异步的，这里挂住别让 React 画出错误态
        await new Promise(() => {});
      }
      throw err;
    }
  };
}

function routeChunk<P extends object>(
  load: () => Promise<{ default: ComponentType<P> }>,
): ComponentType<P> {
  const Lazy = lazy(withChunkReload(load)) as unknown as ComponentType<P>;
  return function Chunked(props: P): JSX.Element {
    return (
      <Suspense fallback={<div className="p-8 text-sm text-slate-400">加载中…</div>}>
        <Lazy {...props} />
      </Suspense>
    );
  };
}

// Real pages 已实现的 /crm/* 路由集合 — placeholder 自动跳过这些
const REAL_CRM_ROUTES = new Set([
  '/crm/inbox',
  '/crm/drafts',
  '/crm/leads',
  '/crm/push-log',
  '/crm/templates',
  '/crm/push-rules',
  '/crm/blocks',
  '/crm/blocks/audit',
  '/crm/tenant-notify',
  '/crm/gateway',
  '/crm/inbound/channels',
  '/crm/inquiry-mailboxes',
  '/crm/inbound-endpoints',
  '/crm/tenants',
  '/crm/tenants/',
  '/crm/onboarding',
  '/crm/whatsapp',
  '/crm/social',
  '/crm/order-flows',
  '/crm/orders',
  '/crm/purchase-orders',
  '/crm/feishu-approvals',
  '/crm/quotes',
  '/crm/binding',
  '/crm/er-audit',
  '/crm/escalation',
  '/crm/decisions',
  '/crm/suppliers',
  '/crm/supplier-health',
  '/crm/products',
  '/crm/dashboard',
  '/crm/match-playground',
  '/crm/salespeople',
  '/crm/migration',
  '/crm/sourcing',
  '/agent/tasks',
]);
const CRM_PLACEHOLDER_ROUTES = getAllModuleChildren().filter(
  (c) => c.to.startsWith('/crm/') && !REAL_CRM_ROUTES.has(c.to),
);

/**
 * 工厂端路由集（G4）。
 *
 * **跟内部路由是两组，按身份二选一挂载，不是挂了再隐藏。**
 *
 * ⚠️ **这是产品体验，不是安全边界。** 租户拿不到内部数据，靠的是后端的
 * `scopeFilter` 和 G3 出口闸 —— 浏览器里绕过前端直接调接口一样能打到后端，
 * 那时候挡住它的是后端。前端分流的真实价值是：工厂不用在六十多个内部入口里
 * 找自己那四个，以及少一点可被探测的面。
 *
 * 别把它当墙。当成墙的话，下一个人会觉得「前端已经分流了后端可以松一点」。
 *
 * 之所以仍然二选一挂载而不是逐项隐藏：逐项隐藏是「默认可见、逐项排除」，
 * 每新增一个内部页面就多一次「租户看不看得见」的判断 —— 那是维护负担，
 * 跟 P1 立的原则（可枚举的一侧走白名单）也不一致。
 */
function PortalRoutes() {
  return (
    <Route
      element={
        <RequireAuth>
          <PortalLayout />
        </RequireAuth>
      }
    >
      <Route path="/portal" element={<PortalHomePage />} />
      {/* 询盘 = 收件箱 IM：无 id 也进会话页(它内部渲染 IM 壳:左联系人+右占位) */}
      <Route path="/portal/inquiries" element={<KeyedPortalConversationPage />} />
      <Route path="/portal/inquiries/:id" element={<KeyedPortalConversationPage />} />
      <Route path="/portal/orders" element={<PortalOrdersPage />} />
      <Route path="/portal/customers" element={<PortalCustomersPage />} />
      <Route path="/portal/customers/new" element={<PortalCustomerNewPage />} />
      <Route path="/portal/customers/import" element={<PortalCustomerImportPage />} />
      <Route path="/portal/site" element={<PortalSitePage />} />
      <Route path="/portal/notify" element={<PortalNotifySettingsPage />} />
      <Route path="/portal/whatsapp" element={<PortalWhatsAppPage />} />
      <Route path="/portal/social" element={<PortalSocialPage />} />
      <Route path="/portal/channels" element={<PortalChannelsPage />} />
      <Route path="/portal/account" element={<PortalAccountPage />} />
      {/* 内部地址在这个身份下不存在，一律回工厂端首页 */}
      <Route path="*" element={<Navigate to="/portal" replace />} />
    </Route>
  );
}

export default function App() {
  const { tenant } = useAuth();
  // 域名先判：portal.link4a.com 上永远只有工厂端，哪怕来的是个内部 session。
  // （实际到不了 —— 内部 cookie 种在 sitebox.globalyiwu.com 上跨不过来 —— 但
  // 「工厂域名上不出现内部后台」这条不该靠 cookie 作用域偶然成立。）
  const portalHost = isPortalHost();
  // session 里有租户身份 → 只挂工厂端那一组
  const isPortal = !!tenant || portalHost;

  return (
    <Routes>
      {/* Public */}
      {/* 工厂用微信打开的待认领填表页 —— 对外页面，不套 admin 布局，也不要求登录 */}
      <Route path="/wechat/bind" element={<BindLandingPage />} />
      {/*
        登录页按**域名**分，不按 session —— 未登录时 session 里什么都没有，
        域名是这一刻唯一能用的信号。工厂端不出现飞书按钮（工厂没有飞书账号，
        而且那个 OAuth 的 redirect_uri 指向 sitebox，在这儿点了也回不来）。
      */}
      <Route
        path="/login"
        element={
          portalHost ? (
            <PortalLoginPage />
          ) : (
            <AppLayout minimal>
              <LoginPage />
            </AppLayout>
          )
        }
      />

      {isPortal ? (
        PortalRoutes()
      ) : (
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<SitesIndexPage />} />
          <Route path="sites">
            <Route path="new" element={<NewSitePage />} />
            <Route path=":slug" element={<SiteDetailPage />} />
            <Route path=":slug/edit" element={<SiteEditPage />} />
            <Route path=":slug/inquiries" element={<SiteInquiriesPage />} />
            <Route path=":slug/analytics" element={<SiteAnalyticsPage />} />
            <Route path=":slug/generate" element={<SiteGeneratePage />} />
            <Route path=":slug/onboarding" element={<SiteOnboardingPage />} />
            <Route path=":slug/precheck" element={<SitePrecheckPage />} />
            <Route path=":slug/tech-config" element={<SiteTechConfigPage />} />
            {/* 旧路径兼容 */}
            <Route path="onboarding/:slug" element={<RedirectToOnboarding />} />
            <Route path="tech-config/:slug" element={<RedirectToTechConfig />} />
          </Route>
          <Route path="account/settings" element={<AccountSettingsPage />} />
          <Route
            path="admin"
            element={
              <RequireAuth adminOnly>
                <AdminPage />
              </RequireAuth>
            }
          />
          <Route
            path="changelog"
            element={
              <RequireAuth adminOnly>
                <ChangelogPage />
              </RequireAuth>
            }
          />
          {/* 自营站 = 一家工厂：消息页直接用工厂端那一屏（同一份组件、同一套后端） */}
          <Route path="strand" element={<SelfOperatedPortal />}>
            <Route path="inquiries" element={<KeyedPortalConversationPage />} />
            <Route path="inquiries/:id" element={<KeyedPortalConversationPage />} />
            <Route path="whatsapp" element={<PortalWhatsAppPage />} />
            <Route path="social" element={<PortalSocialPage />} />
            {/* 10-26：渠道 —— 工厂端同一页；自营那家能直接连 Facebook */}
            <Route path="channels" element={<PortalChannelsPage />} />
            {/* 10-23：采购单进度、超时提醒推到这里配的通道（跟工厂端「通知设置」同一份配置） */}
            <Route path="notify" element={<PortalNotifySettingsPage />} />
          </Route>
          {/* CRM real pages (sprint-admin-ui iter 2) */}
          <Route path="crm/inbox" element={<InboxPage />} />
          <Route path="crm/inbox/:id" element={<InboxDetailPage />} />
          <Route path="crm/drafts" element={<DraftsPage />} />
          {/* CRM real pages (sprint-admin-ui iter 3) */}
          {/* 旧版消息 10-23 下线：飞书历史消息、书签里还有这两条链接，跳到「消息」页同一个客户 */}
          <Route path="crm/leads" element={<Navigate to="/strand/inquiries" replace />} />
          <Route path="crm/leads/:id" element={<LegacyLeadRedirect />} />
          <Route path="crm/inquiry/:id" element={<InquiryRedirectPage />} />
          <Route path="crm/push-log" element={<PushLogPage />} />
          {/* CRM real pages (sprint-admin-ui iter 4) */}
          <Route path="crm/templates" element={<TemplatesPage />} />
          <Route path="crm/push-rules" element={<PushRulesPage />} />
          <Route path="crm/blocks" element={<BlocksPage />} />
          {/* CRM real pages (sprint-admin-ui iter 5) */}
          <Route path="crm/blocks/audit" element={<BlocksAuditPage />} />
          <Route path="crm/tenant-notify" element={<TenantNotifyLogPage />} />
          <Route path="crm/gateway" element={<GatewayPage />} />
          <Route path="crm/inbound/channels" element={<InboundChannelStatusPage />} />
          <Route path="crm/outreach" element={<OutreachFunnelPage />} />
          <Route path="crm/factory-outreach" element={<FactoryOutreachPage />} />
          <Route path="crm/inquiry-mailboxes" element={<InquiryMailboxesPage />} />
          <Route path="crm/inbound-endpoints" element={<InboundEndpointsPage />} />
          <Route path="crm/field-candidates" element={<FieldCandidatesPage />} />
          {/* 付费工厂从签约到跑起来那条线的主界面；下面几条是它的明细页 */}
          <Route path="crm/onboarding" element={<FactoryOnboardingPage />} />
          <Route path="crm/tenants" element={<TenantsPage />} />
          <Route path="crm/tenant-assignment" element={<TenantAssignmentPage />} />
          <Route path="crm/tier-capabilities" element={<TierCapabilitiesPage />} />
          <Route path="crm/tenants/:id" element={<TenantDetailPage />} />
          <Route path="crm/order-flows" element={<OrderFlowsPage />} />
          <Route path="crm/order-flows/:key" element={<OrderFlowEditPage />} />
          <Route path="crm/quotes" element={<QuotesPage />} />
          <Route path="crm/orders" element={<OrdersPage />} />
          <Route path="crm/orders/new" element={<OrderCreatePage />} />
          <Route path="crm/orders/import" element={<OrderImportPage />} />
          <Route path="crm/orders/:id" element={<OrderDetailPage />} />
          <Route path="crm/purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="crm/feishu-approvals" element={<FeishuApprovalsPage />} />
          <Route path="crm/binding" element={<BindingDashboardPage />} />
          {/*
            WhatsApp 通道的三个页签。一条菜单三个页签，路径也就得同源 ——
            侧边栏按前缀高亮，/crm/wa-relay 那种平行路径会让菜单在页签之间跳来跳去。
            我们自己那一路的操作台（绑/扫/解绑）是默认页签。
          */}
          <Route path="crm/whatsapp" element={<WaSelfBindPage />} />
          <Route path="crm/whatsapp/relay" element={<WaRelayStatusPage />} />
          <Route path="crm/whatsapp/egress" element={<WaEgressPage />} />
          <Route path="crm/social" element={<SocialCommentsPage />} />
          <Route path="crm/channels" element={<ChannelsOverviewPage />} />
          {/* 旧路径留着跳转：文档、飞书消息里发出去过这两条链接 */}
          <Route path="crm/wa-relay" element={<Navigate to="/crm/whatsapp/relay" replace />} />
          <Route path="crm/wa-egress" element={<Navigate to="/crm/whatsapp/egress" replace />} />
          <Route path="crm/er-audit" element={<ErAuditPage />} />
          {/* CRM real pages (sprint-admin-ui iter 6) */}
          <Route path="crm/escalation" element={<EscalationPage />} />
          <Route path="crm/decisions" element={<DecisionsPage />} />
          {/* 供应侧 (C2 摄入数据) + Dashboard */}
          <Route path="crm/dashboard" element={<DashboardPage />} />
          <Route path="crm/suppliers" element={<SuppliersPage />} />
          <Route path="crm/suppliers/:id" element={<SupplierDetailPage />} />
          <Route path="crm/supplier-health" element={<SupplierHealthPage />} />
          <Route path="crm/products" element={<ProductsPage />} />
          <Route path="crm/products/:supplierCode/:factorySku" element={<ProductDetailPage />} />
          <Route path="crm/match-playground" element={<MatchPlaygroundPage />} />
          <Route path="crm/salespeople" element={<SalespeoplePage />} />
          <Route path="crm/cases/:customerId" element={<CasePage />} />
          <Route path="crm/drafts/:id/edit" element={<DraftEditPage />} />
          {/* C6 供应商迁移 + 通用 Agent 任务（c6-migration-agent iter5） */}
          <Route path="crm/migration" element={<MigrationListPage />} />
          <Route path="crm/migration/:id" element={<MigrationDetailPage />} />
          <Route path="crm/sourcing" element={<SourcingListPage />} />
          <Route path="crm/sourcing/:id" element={<SourcingDetailPage />} />
          {/* H6 竞对情报闭环的可视化入口 */}
          <Route path="seo/competitors" element={<CompetitorsPage />} />
          <Route path="seo/citations" element={<CitationsPage />} />
          <Route path="seo/competitors/:eventId" element={<ReportDetailPage />} />
          <Route path="seo/topics" element={<TopicsPage />} />
          <Route path="seo/runs" element={<RunsPage />} />
          <Route path="seo/article-index" element={<ArticleIndexPage />} />
          <Route path="agent/tasks" element={<TaskListPage />} />
          <Route path="agent/tasks/:id" element={<TaskDetailPage />} />
          {/* CRM placeholder routes — replaced by real pages in later iters */}
          {CRM_PLACEHOLDER_ROUTES.map((child) => (
            <Route
              key={child.id}
              path={child.to.replace(/^\//, '')}
              element={<PlaceholderPage title={child.label} />}
            />
          ))}

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
  );
}

function RedirectToOnboarding() {
  const slug = window.location.pathname.split('/').filter(Boolean).pop();
  return <Navigate to={`/sites/${slug}/onboarding`} replace />;
}

function RedirectToTechConfig() {
  const slug = window.location.pathname.split('/').filter(Boolean).pop();
  return <Navigate to={`/sites/${slug}/tech-config`} replace />;
}
